import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Typography, Box, Tooltip, Snackbar, IconButton, Alert, Button } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useGridApiRef } from '@mui/x-data-grid-pro';
import ControlledDataGrid from '../../components/DataGrid/ControlledDataGrid';
import { jsonToCSV } from "react-papaparse";
import { CSVDownloader } from "react-papaparse";
import { AnalyticsBrowser } from '@segment/analytics-next'
import { SEGMENTAPIKEY } from '../../config';
import TimesheetsFilter from "./TimesheetsFilter";
import moment from "moment-timezone";
import axios from "axios";
import { APIURL } from "../../config";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
import { OfficeLocationFilter, ProgramFilter, ReportFilterBar } from "./ReportFilters";
import { convertTime } from "src/utils/time";

const analytics = AnalyticsBrowser.load({ writeKey: SEGMENTAPIKEY })

const DEFAULT_GRID_ID = 45;
const DEFAULT_PAGE_SIZE = 50;
const REPORT_NAME = "Time Management Report";
const EMPTY_NOTE_VALUES = [null, "undefined", "N/A"];
const EMPTY_FILTERS = { dateRange: [null, null], olocId: null, programId: null };

const dateComparator = (v1, v2) => moment(v1).diff(moment(v2));

const formatDate = (value) =>
    moment(value).isValid() ? moment(value).local().format("MM/DD/YYYY") : "-";

/**
 * Renders a stored timestamp in the viewer's timezone, converting from the timezone the record was
 * captured in. Returns null when the record's timezone is unknown or the value can't be converted,
 * so callers can fall back to their own formatting.
 */
const toViewerTime = (value, fromTz, toTz) => {
    const converted = convertTime(value, fromTz, toTz, "h:mm a");
    return converted && converted !== "Invalid date" ? converted : null;
};

/** Scheduled task times, captured in the assigned employee's timezone. */
const formatTaskTime = (value, fromTz, toTz) =>
    toViewerTime(value, fromTz, toTz) ?? (value ? (moment(value).isValid() ? moment(value).local().format("h:mm a") : value) : "-");

/** Recorded check-in/check-out times, captured in the timezone the employee was located in. */
const formatRecordTime = (value, fromTz, toTz) =>
    toViewerTime(value, fromTz, toTz) ?? (moment(value).isValid() ? moment(String(value).replace("Z", "")).local().format("h:mm a") : "-");

const formatComments = (comments) => {
    if (!Array.isArray(comments)) return "";

    return comments
        .map((comment) => {
            try {
                const json = JSON.parse(comment.comment_text);
                if (json.blocks && Array.isArray(json.blocks)) {
                    return json.blocks.map((block) => block.text).join(' ');
                }
            } catch (e) {
                // Not a valid JSON, continue with the text as is
            }
            return comment.comment_text;
        })
        .join(' | ');
};

/** Whole hours between two timestamps, as reported in the cumulative-hours columns. */
const hoursBetween = (start, end) => moment(end).diff(moment(start), "h");

/**
 * Renders an hours value from the API. taskHrs comes back null when the record has no check-out
 * time, so the row still needs to render instead of throwing on .toFixed().
 */
const formatHours = (value) => {
    const hours = Number(value);
    return value === null || value === undefined || Number.isNaN(hours) ? "0.00" : hours.toFixed(2);
};

/** Hours between two timestamps to the minute, or null when either end is unusable. */
const decimalHoursBetween = (start, end) => {
    if (!moment(start).isValid() || !moment(end).isValid()) return null;

    return moment(end).diff(moment(start), "m") / 60;
};

/** Maps one API timesheet record to the row shape used by the grid, the CSV export and the PDF. */
const buildRow = (timesheet, localTz) => {
    const { paymentDets } = timesheet;

    const employeeHrs = hoursBetween(timesheet.start_time, timesheet.end_time);

    const scheduledHrs = decimalHoursBetween(timesheet.tas_start_time, timesheet.tas_end_time);
    const recordedHrs = decimalHoursBetween(timesheet.start_time, timesheet.end_time);

    const overtimeHrs = scheduledHrs !== null && recordedHrs !== null
        ? Math.max(recordedHrs - scheduledHrs, 0)
        : 0;

    return {
        id: timesheet.id,
        emp_id: timesheet.emp_id,
        empName: timesheet.emp_first_name ? timesheet.emp_first_name + ' ' + timesheet.emp_last_name : '',
        FirstName: timesheet.emp_first_name,
        LastName: timesheet.emp_last_name,
        EmpTitle: paymentDets ? paymentDets.pea_status : "N/A",
        EmpType: timesheet.emp_type,
        ProjectTime: timesheet.pro_created_at ? moment(timesheet.pro_created_at).format("MM/DD/YYYY") : "-",
        PaymentType: paymentDets ? paymentDets.pea_frequency : "N/A",
        TaskcreateDate: timesheet.tas_created_at || "-",
        Project: timesheet.pro_title || "-",
        TaskStartDate: timesheet.tas_start_date ? moment(timesheet.tas_start_date).local().format("MM/DD/YYYY") : "-",
        TaskStartTime: formatTaskTime(timesheet.tas_start_time, timesheet.task_emp_timezone, localTz),
        RecActivityDate: moment(timesheet.start_time).isValid() ? moment(timesheet.start_time).format("MM/DD/YYYY") : "-",
        RecStartTime: formatRecordTime(timesheet.start_time, timesheet.empLocate_timezone, localTz),
        TaskName: timesheet.tas_title || "-",
        TaskType: timesheet.tas_type || "-",
        RegularHrs: scheduledHrs !== null ? scheduledHrs.toFixed(2) : 0,
        WeeklyHrs: formatHours(timesheet.taskHrs),
        OvertimeHrs: overtimeHrs.toFixed(2),
        ProjectHrs: timesheet.pro_starts_on ? moment().diff(moment(timesheet.pro_starts_on), "h") : "-",
        EmployeeHrs: employeeHrs,
        HourlyRate: paymentDets ? "$ " + paymentDets.pea_bill_rate : "$ 0",
        TotalHourlyPay: "$ " + (paymentDets
            ? (moment.duration(moment(timesheet.end_time).diff(moment(timesheet.start_time))).asHours().toFixed(2)
                * paymentDets.pea_bill_rate).toFixed(2)
            : "-"),
        TaskEndDate: timesheet.tas_end_date ? moment(timesheet.tas_end_date).local().format("MM/DD/YYYY") : "-",
        TaskEndTime: formatTaskTime(timesheet.tas_end_time, timesheet.task_emp_timezone, localTz),
        RecEndTime: formatRecordTime(timesheet.end_time, timesheet.empLocate_timezone, localTz),
        PayrollEndDate: paymentDets ? moment(paymentDets.pea_associate_to).local().format("MM/DD/YYYY h:mm a") : "-",
        TaskNotes: EMPTY_NOTE_VALUES.includes(timesheet.tas_notes) ? "" : timesheet.tas_notes,
        CaseNotes: formatComments(timesheet.taskComments),
    };
};

const toCsvRow = (row) => ({
    id: row.id,
    empName: row.empName,
    TaskStartTime: row.TaskStartTime,
    RecActivityDate: row.RecActivityDate,
    RecStartTime: row.RecStartTime,
    TaskEndTime: row.TaskEndTime,
    RecEndTime: row.RecEndTime,
    "First Name": row.FirstName,
    "Last Name": row.LastName,
    "SC": row.emp_id,
    "Title": row.EmpTitle,
    "Classification": row.EmpType,
    "Project Creation Date": row.ProjectTime,
    "Payment Type": row.PaymentType,
    "Date Entered": row.TaskcreateDate,
    "Start Date of Service": row.TaskStartDate,
    "Start Time of Service": row.TaskStartTime,
    "Task Name": row.TaskName.replace(/#/g, ""),
    "Account Code": row.TaskType,
    "Regular Hrs": row.RegularHrs,
    "Weekly Hrs": row.WeeklyHrs,
    "Overtime Hrs": row.OvertimeHrs,
    "Project (Cum Hrs)": row.ProjectHrs,
    "Employee (Cum Hrs)": row.EmployeeHrs,
    "Hourly Rate": row.HourlyRate,
    "Total Hourly Pay": row.TotalHourlyPay,
    "End Date of Service": row.TaskEndDate,
    "End Time of Service": row.TaskEndTime,
    "Payroll End Date": row.PayrollEndDate,
    "Comments": row.TaskNotes,
    "Notes": row.CaseNotes,
});

const truncatedCell = (params) => (
    <Tooltip title={params.value}>
        <span
            className="truncate-text"
            style={{
                lineHeight: "1",
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
                alignItems: "center",
            }}
        >
            {params.value}{" "}
        </span>
    </Tooltip>
);

const wrappedCell = (params) => (
    <Tooltip title={params.value}>
        <span className="table-cell-trucate text-wrap" style={{ lineHeight: "1" }}>
            {params.value}
        </span>
    </Tooltip>
);

const columns = [
    {
        field: "TaskcreateDate",
        headerName: "Date Entered",
        flex: 1.0,
        renderCell: (params) => <div>{formatDate(params.value)}</div>,
        valueFormatter: (value) => formatDate(value),
    },
    { field: "Project", headerName: "Project", flex: 1.3, renderCell: wrappedCell },
    { field: "TaskName", headerName: "Task Name", flex: 1.3, renderCell: wrappedCell },
    { field: "TaskType", headerName: "Type", flex: 1.0 },
    { field: "FirstName", headerName: "Employee First Name", flex: 1.5 },
    { field: "LastName", headerName: "Employee Last Name", flex: 1.5 },
    { field: "empName", headerName: "Employee Name", flex: 1.5 },
    { field: "RecActivityDate", headerName: "Activity Date", flex: 1.0, sortComparator: dateComparator },
    { field: "RecStartTime", headerName: "Start Time", flex: 1.0 },
    { field: "RecEndTime", headerName: "End Time", flex: 1.0 },
    {
        field: "WeeklyHrs",
        headerName: "Hours",
        flex: 0.53,
        sortComparator: (v1, v2) => parseFloat(v1) - parseFloat(v2),
    },
    { field: "TaskNotes", headerName: "Comments", flex: 2.0, renderCell: truncatedCell },
    { field: "CaseNotes", headerName: "Notes", flex: 2.0, renderCell: truncatedCell },
];

const DEFAULT_COLUMN_VISIBILITY = { FirstName: false, LastName: false };

const escapeHtml = (value) =>
    String(value ?? "").replace(/[&<>]/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[char]));

const printRows = (rows) => {
    const cellStyle = "width:100%; border-bottom: 1px solid black";
    const header = columns
        .map((col) => `<th style='${cellStyle}'>${escapeHtml(col.headerName)}</th>`)
        .join("");
    const body = rows
        .map((row) => {
            const cells = columns
                .map((col) => {
                    const value = col.valueFormatter ? col.valueFormatter(row[col.field]) : row[col.field];
                    return `<td style='${cellStyle}'>${escapeHtml(value)}</td>`;
                })
                .join("");
            return `<tr>${cells}</tr>`;
        })
        .join("");

    const newWin = window.open("");
    if (!newWin) return;

    newWin.document.write(`<table><tr>${header}</tr>${body}</table>`);
    newWin.print();
    newWin.close();
};

const StatusSnackbar = ({ message, severity, onClose }) => (
    <Snackbar open={message !== ""} autoHideDuration={30000} onClose={onClose}>
        <Alert sx={{ width: "100%" }} severity={severity} elevation={6} variant="filled">
            {message}
            <IconButton aria-label="close" color="inherit" sx={{ p: 0.25 }} onClick={onClose}>
                <CloseIcon />
            </IconButton>
        </Alert>
    </Snackbar>
);

const TimesheetsReport = React.forwardRef((props, ref) => {
    const { user, comp_id, customReportGridId } = props;
    const gridId = customReportGridId || DEFAULT_GRID_ID;

    const apiRef = useGridApiRef();
    useImperativeHandle(ref, () => apiRef.current);

    const [timesheets, setTimesheets] = useState([]);
    const [count, setCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [filterResults, setFilterResults] = useState({});
    const [filterResultsOutput, setFilterResultsOutput] = useState("");
    const [isCustomReportOpen, setIsCustomReportOpen] = useState(false);
    const [dateRange, setDateRange] = useState([null, null]);
    const [selectedOffice, setSelectedOffice] = useState(null);
    const [selectedProgram, setSelectedProgram] = useState(null);
    const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
    const [reportDetails, setReportDetails] = useState({});
    const [isFavorite, setIsFavorite] = useState(false);
    const [successText, setSuccessText] = useState("");
    const [errorText, setErrorText] = useState("");

    const hasLoggedToSegment = useRef(false);

    const fetchReportDetails = useCallback(async () => {
        try {
            const response = await axios.get(APIURL + "/jsreport/getAllReports", {
                params: { empID: user.emp_id },
            });
            const report = response?.data?.reports?.find((ele) => ele.report_name === REPORT_NAME);
            setReportDetails(report);
            setIsFavorite(report?.status === 1);
        } catch (error) {
            console.error("Failed to load report details", error);
        }
    }, [user.emp_id]);

    useEffect(() => {
        let isCurrent = true;
        const [start, end] = appliedFilters.dateRange || [null, null];

        setLoading(true);

        axios
            .get(APIURL + "/reports/getTimesheets", {
                params: {
                    comp_id,
                    emp_id: user.emp_id,
                    emp_type: user.type,
                    page,
                    page_size: pageSize,
                    customFilter: filterResults,
                    start_date: start ? start.format("YYYY-MM-DD") : undefined,
                    end_date: end ? end.format("YYYY-MM-DD") : undefined,
                    oloc_id: appliedFilters.olocId || undefined,
                    program_id: appliedFilters.programId || undefined,
                },
            })
            .then((response) => {
                if (!isCurrent) return;
                setTimesheets(response.data?.timesheetsData ?? []);
                setCount(response.data?.count ?? 0);
            })
            .catch((error) => {
                if (!isCurrent) return;
                console.error("Failed to load timesheets", error);
                setErrorText("Unable to load timesheets. Please try again.");
            })
            .finally(() => {
                if (!isCurrent) return;
                setLoading(false);
                setHasLoaded(true);
            });

        return () => {
            isCurrent = false;
        };
    }, [comp_id, user.emp_id, user.type, page, pageSize, filterResults, appliedFilters]);

    useEffect(() => {
        fetchReportDetails();

        if (!hasLoggedToSegment.current) {
            hasLoggedToSegment.current = true;

            let pageName = String(window.location.href).split("/").pop();
            if (pageName.includes("fieldworker") || pageName === "" || pageName === "#") {
                pageName = "Home";
            } else {
                pageName = String(pageName).charAt(0).toUpperCase() + String(pageName).toLowerCase().slice(1);
            }

            const title = pageName + " : Timesheets Report";
            analytics.page(title, { title, path: title }, { userId: String(user.emp_id) });
        }

        localStorage.removeItem("timesheetFilterData");
    }, [fetchReportDetails, user.emp_id]);

    // The date range is applied server-side, so every returned row is already in range.
    const rows = useMemo(() => {
        const localTz = moment.tz.guess();
        return timesheets.map((timesheet) => buildRow(timesheet, localTz));
    }, [timesheets]);

    const csvTimesheet = useMemo(() => jsonToCSV(rows.map(toCsvRow)), [rows]);

    const hasActiveFilters = Boolean(
        appliedFilters.olocId
        || appliedFilters.programId
        || appliedFilters.dateRange?.[0]
        || appliedFilters.dateRange?.[1]
        || filterResultsOutput !== ""
    );

    const handleSearch = () => {
        setAppliedFilters({ dateRange, olocId: selectedOffice, programId: selectedProgram });
        setPage(0);
    };

    const handleFilterForm = (filterValues) => {
        setFilterResults(filterValues);
        setFilterResultsOutput(Object.values(filterValues).filter(Boolean).join(", "));
        setIsCustomReportOpen(false);
        setPage(0);
    };

    const handlePaginationModelChange = (model) => {
        // reset to the first page when the page size changes
        setPage(model.pageSize !== pageSize ? 0 : model.page);
        setPageSize(model.pageSize);
    };

    const markFavourite = async (report, status) => {
        try {
            const response = await axios.put(APIURL + "/jsreport/markFavorite", {
                report_id: report?.report_id,
                status,
                empID: user.emp_id,
            });

            if (response?.status === 200 && response?.data?.code === 200) {
                setSuccessText(response?.data?.status_message || "Favorite Modifed Succesfully");
            } else {
                setErrorText("Something went wrong");
            }
        } catch (error) {
            setErrorText("Something went wrong");
        }

        fetchReportDetails();
    };

    return (
        <div>
            {!hasLoaded ? (
                <div style={{ display: 'flex', justifyContent: 'center', margin: 10, marginTop: 50, width: '75vw' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">Loading...</span>
                    </div>
                </div>
            ) : timesheets.length === 0 && !hasActiveFilters ? (
                <div
                    className="d-flex flex-column justify-content-center align-items-center"
                    style={{ height: "500px", marginTop: 10 }}
                >
                    <img
                        src="./Timesheetwhite.png"
                        alt="Timesheetwhite"
                        style={{ width: "20rem", height: "15rem", margin: 10 }}
                    />
                    <h5
                        style={{ fontFamily: "Lato", marginTop: 48, marginBottom: 30 }}
                        className="text-center"
                    >
                        No timesheets entry to show{" "}
                    </h5>
                    <div className="d-flex flex-row justify-content-between">
                        <div className="m-2 mr-2">
                            <span
                                style={{ fontFamily: "Lato", fontSize: 14, width: "70%" }}
                                className="text-muted text-center text-wrap"
                            >
                                Your team haven’t completed any tasks yet. Once they have,
                                you’ll see detailed entries here.
                            </span>
                        </div>
                    </div>
                </div>
            ) : (
                <div style={{ margin: 0, width: '100%', paddingLeft: 10, paddingRight: 10, boxSizing: 'border-box' }}>
                    {isCustomReportOpen ? (
                        <TimesheetsFilter
                            closeCustomReport={() => setIsCustomReportOpen(false)}
                            onFormSubmission={handleFilterForm}
                        />
                    ) : null}
                    <div>
                        <Typography
                            style={{
                                fontWeight: "bold",
                                fontSize: "1.5rem",
                                fontFamily: "Lato",
                            }}
                        >
                            {REPORT_NAME}
                        </Typography>
                        <div style={{ display: 'flex', marginBottom: 10, justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <ReportFilterBar onSearch={handleSearch}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs} localeText={{ start: 'From Date', end: 'To Date' }}>
                                        <DateRangePicker
                                            value={dateRange}
                                            onChange={setDateRange}
                                            slotProps={{ fieldSeparator: { children: 'to' }, textField: { size: 'small' } }}
                                        />
                                    </LocalizationProvider>
                                    <OfficeLocationFilter
                                        comp_id={comp_id}
                                        value={selectedOffice}
                                        onChange={setSelectedOffice}
                                    />
                                    <ProgramFilter
                                        comp_id={comp_id}
                                        value={selectedProgram}
                                        onChange={setSelectedProgram}
                                    />
                                </ReportFilterBar>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => {
                                        if (isCustomReportOpen) {
                                            handleFilterForm({});
                                            localStorage.removeItem("timesheetFilterData");
                                        } else {
                                            setIsCustomReportOpen(true);
                                        }
                                    }}
                                    sx={{ ml: 1 }}
                                >
                                    {isCustomReportOpen ? 'Reset' : 'Custom Report'}
                                </Button>
                                {filterResultsOutput !== '' && (
                                    <Typography
                                        variant="body2"
                                        sx={{ fontStyle: 'italic', color: '#626262', ml: 2 }}
                                    >
                                        Reports results: {filterResultsOutput}
                                    </Typography>
                                )}
                            </div>
                            <div>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-info m-1"
                                >
                                    <CSVDownloader
                                        data={csvTimesheet}
                                        filename={"timesheet report"}
                                        bom={true}
                                    >
                                        CSV
                                    </CSVDownloader>
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-info m-1"
                                    onClick={() => printRows(rows)}
                                >
                                    PDF
                                </button>
                                <Tooltip title={isFavorite ? "Remove from favorite reports" : "Add to favorite reports"}>
                                    <img
                                        src={isFavorite ? "./star-solid.svg" : "./star-regular.svg"}
                                        alt="barchart"
                                        style={{
                                            fontFamily: "Lato",
                                            width: "1.5rem",
                                            height: "1.5rem",
                                            cursor: "pointer",
                                            margin: 5
                                        }}
                                        onClick={() => markFavourite(reportDetails, isFavorite ? 0 : 1)}
                                    />
                                </Tooltip>
                            </div>
                        </div>
                        <div id="printTable" style={{ width: '100%', overflowX: 'auto', overflowY: 'hidden' }}>
                            <Box sx={{ width: '100%', height: "calc(100vh - 180px)" }}>
                                <ControlledDataGrid
                                    gridId={gridId}
                                    user={user}
                                    loading={loading}
                                    columnVisibilityModel={DEFAULT_COLUMN_VISIBILITY}
                                    isRowSelectable={(params) => (params.row.id < 0)}
                                    initialState={{ sorting: { sortModel: [{ field: "TaskcreateDate", sort: "desc" }] } }}
                                    apiRef={apiRef}
                                    disableColumnFilter
                                    pagination
                                    paginationMode="server"
                                    rowCount={count}
                                    paginationModel={{ page, pageSize }}
                                    onPaginationModelChange={handlePaginationModelChange}
                                    pageSizeOptions={[25, 50, 100]}
                                    filterMode="client"
                                    style={{ color: "#000000" }}
                                    getRowClassName={(params) =>
                                        params.indexRelativeToCurrentPage % 2 === 1
                                            ? "time-management-row-alternate"
                                            : ""
                                    }
                                    sx={{
                                        '& .time-management-row-alternate .MuiDataGrid-cell, & .time-management-row-alternate .MuiDataGrid-filler--pinnedLeft': {
                                            backgroundColor: '#EAF4FC',
                                        },
                                        '& .time-management-row-alternate:hover .MuiDataGrid-cell, & .time-management-row-alternate:hover .MuiDataGrid-filler--pinnedLeft': {
                                            backgroundColor: '#DCEFFC',
                                        },
                                    }}
                                    rows={rows}
                                    columns={columns}
                                />
                            </Box>
                        </div>
                    </div>
                </div>
            )}
            <StatusSnackbar message={successText} severity="success" onClose={() => setSuccessText("")} />
            <StatusSnackbar message={errorText} severity="error" onClose={() => setErrorText("")} />
        </div>
    );
});

export default TimesheetsReport;
